import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeftIcon, 
  BuildingOfficeIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  MapPinIcon,
  DocumentTextIcon,
  ClockIcon,
  TruckIcon,
  CurrencyDollarIcon,
  StarIcon as StarIconOutline,
  PencilIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import { useAppData } from '../../contexts/AppDataContext';
import SupplierNegotiations from './SupplierNegotiations';
import SupplierPerformance from './SupplierPerformance';
import SupplierContracts from './SupplierContracts';

const SupplierDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    getSupplierById, 
    getPurchases, 
    getProducts
  } = useAppData();
  
  const [supplier, setSupplier] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [supplierPurchases, setSupplierPurchases] = useState([]);
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [hasData, setHasData] = useState({
    purchases: false,
    products: false
  });

  // Charger les informations du fournisseur et les données associées
  useEffect(() => {
    setIsLoading(true);
    
    // Récupérer les données des fournisseurs du localStorage avec la bonne clé
    setTimeout(() => {
      // 1. Obtenir les données du fournisseur
      const storedSuppliers = JSON.parse(localStorage.getItem('stockpro_suppliers')) || [];
      console.log('Suppliers loaded from localStorage:', storedSuppliers.length);
      console.log('Looking for supplier with ID:', id);
      
      const foundSupplier = storedSuppliers.find(s => s.id === id);
      console.log('Found supplier:', foundSupplier);
      
      if (foundSupplier) {
        // Adapter les propriétés du fournisseur si nécessaire
        const supplierData = {
          ...foundSupplier,
          contactName: foundSupplier.contact || foundSupplier.contactName,
          since: foundSupplier.since || '2021-05-10',
          totalSpent: foundSupplier.totalSpent || foundSupplier.orders * 200000,
          qualityRating: foundSupplier.qualityRating || 4.2,
          priceCompetitiveness: foundSupplier.priceCompetitiveness || 4.0,
          communicationRating: foundSupplier.communicationRating || 4.3,
          paymentTerms: foundSupplier.paymentTerms || '30days',
          nif: foundSupplier.nif || '19638521400157',
          rc: foundSupplier.rc || 'RC/16/00-0123456B22',
          ai: foundSupplier.ai || '1639480',
          notes: foundSupplier.notes || `Fournisseur de ${foundSupplier.category}. Relation commerciale établie.`,
          website: foundSupplier.website || `www.${foundSupplier.name.toLowerCase().replace(/\s/g, '')}.dz`
        };
        
        setSupplier(supplierData);
        
        // 2. Récupérer les achats liés à ce fournisseur
        const allPurchases = JSON.parse(localStorage.getItem('stockpro_purchases')) || [];
        // Si le stockage est vide, on peut utiliser des données mockées pour l'interface
        const mockPurchases = JSON.parse(localStorage.getItem('purchases')) || [];
        
        // Filtrer les achats liés à ce fournisseur par ID ou par nom
        const filteredPurchases = allPurchases.filter(purchase => 
          (purchase.supplierId === id) || 
          (purchase.supplier === foundSupplier.name)
        );
        
        setSupplierPurchases(filteredPurchases);
        setHasData({...hasData, purchases: filteredPurchases.length > 0});

        // 3. Récupérer les produits fournis par ce fournisseur
        const allProducts = JSON.parse(localStorage.getItem('stockpro_products')) || 
                           JSON.parse(localStorage.getItem('products')) || [];
        
        // Filtrer les produits liés à ce fournisseur
        const filteredProducts = allProducts.filter(product => 
          (product.supplierId === id) || 
          (product.supplier === foundSupplier.name)
        );
        
        setSupplierProducts(filteredProducts);
        setHasData({...hasData, products: filteredProducts.length > 0});
        
      } else {
        // Fournisseur non trouvé
        toast.error('Fournisseur introuvable');
        navigate('/suppliers');
      }
      
      setIsLoading(false);
    }, 400);
  }, [id, navigate]);

  // Produits fallback en cas d'absence de données réelles
  const getFallbackProducts = (category) => {
    const fallbackProductsByCategory = {
      'Papeterie': [
        { 
          id: 'PROD-001', 
          name: 'Papier A4 Premium (500 feuilles)', 
          category: 'Papeterie', 
          price: 850, 
          unit: 'Rame',
          lastOrder: '2023-07-05',
          inStock: true,
          isPlaceholder: true
        },
        { 
          id: 'PROD-011', 
          name: 'Cahier A4 quadrillé', 
          category: 'Papeterie', 
          price: 150, 
          unit: 'Unité',
          lastOrder: '2023-06-30',
          inStock: true,
          isPlaceholder: true
        }
      ],
      'Informatique': [
        { 
          id: 'PROD-101', 
          name: 'Cartouche d\'encre HP', 
          category: 'Informatique', 
          price: 3500, 
          unit: 'Unité',
          lastOrder: '2023-07-10',
          inStock: true,
          isPlaceholder: true
        },
        { 
          id: 'PROD-102', 
          name: 'Clavier sans fil Logitech', 
          category: 'Informatique', 
          price: 4200, 
          unit: 'Unité',
          lastOrder: '2023-06-25',
          inStock: true,
          isPlaceholder: true
        }
      ],
      'Fournitures': [
        { 
          id: 'PROD-201', 
          name: 'Stylo à bille (lot de 50)', 
          category: 'Fournitures', 
          price: 1200, 
          unit: 'Lot',
          lastOrder: '2023-06-22',
          inStock: true,
          isPlaceholder: true
        },
        { 
          id: 'PROD-202', 
          name: 'Classeur A4', 
          category: 'Fournitures', 
          price: 380, 
          unit: 'Unité',
          lastOrder: '2023-07-15',
          inStock: true,
          isPlaceholder: true
        }
      ],
      'Électronique': [
        { 
          id: 'PROD-301', 
          name: 'Rallonge électrique 5m', 
          category: 'Électronique', 
          price: 1200, 
          unit: 'Unité',
          lastOrder: '2023-07-01',
          inStock: true,
          isPlaceholder: true
        },
        { 
          id: 'PROD-302', 
          name: 'Chargeur universel 65W', 
          category: 'Électronique', 
          price: 3500, 
          unit: 'Unité',
          lastOrder: '2023-06-28',
          inStock: true,
          isPlaceholder: true
        }
      ],
      'Mobilier': [
        { 
          id: 'PROD-401', 
          name: 'Chaise de bureau ergonomique', 
          category: 'Mobilier', 
          price: 18500, 
          unit: 'Unité',
          lastOrder: '2023-06-15',
          inStock: true,
          isPlaceholder: true
        },
        { 
          id: 'PROD-402', 
          name: 'Bureau 140x80cm', 
          category: 'Mobilier', 
          price: 25000, 
          unit: 'Unité',
          lastOrder: '2023-05-20',
          inStock: true,
          isPlaceholder: true
        }
      ],
      'Entretien': [
        { 
          id: 'PROD-501', 
          name: 'Produit nettoyant multi-surface (5L)', 
          category: 'Entretien', 
          price: 1800, 
          unit: 'Bidon',
          lastOrder: '2023-07-08',
          inStock: true,
          isPlaceholder: true
        },
        { 
          id: 'PROD-502', 
          name: 'Lingettes désinfectantes (pack 100)', 
          category: 'Entretien', 
          price: 950, 
          unit: 'Pack',
          lastOrder: '2023-07-12',
          inStock: true,
          isPlaceholder: true
        }
      ]
    };
    
    // Retourner les produits pour la catégorie ou une liste par défaut
    return fallbackProductsByCategory[category] || fallbackProductsByCategory['Papeterie'];
  };

  // Commandes fallback en cas d'absence de données réelles
  const getFallbackOrders = (category) => {
    const fallbackOrdersByCategory = {
      'Papeterie': [
        {
          id: 'CMD-2023-0125',
          date: '2023-07-15',
          amount: 45600,
          status: 'Livrée',
          items: [
            { name: 'Papier A4 Premium', quantity: 50, price: 850 }
          ],
          isPlaceholder: true
        },
        {
          id: 'CMD-2023-0118',
          date: '2023-06-30',
          amount: 24000,
          status: 'Livrée',
          items: [
            { name: 'Enveloppes A4', quantity: 5, price: 1600 }
          ],
          isPlaceholder: true
        }
      ],
      'Informatique': [
        {
          id: 'CMD-2023-0156',
          date: '2023-07-12',
          amount: 38000,
          status: 'Livrée',
          items: [
            { name: 'Cartouche d\'encre HP', quantity: 8, price: 3500 }
          ],
          isPlaceholder: true
        },
        {
          id: 'CMD-2023-0144',
          date: '2023-06-28',
          amount: 50400,
          status: 'Livrée',
          items: [
            { name: 'Clavier sans fil Logitech', quantity: 12, price: 4200 }
          ],
          isPlaceholder: true
        }
      ],
      'Fournitures': [
        {
          id: 'CMD-2023-0172',
          date: '2023-07-15',
          amount: 31800,
          status: 'Livrée',
          items: [
            { name: 'Stylo à bille (lot de 50)', quantity: 10, price: 1200 }
          ],
          isPlaceholder: true
        },
        {
          id: 'CMD-2023-0168',
          date: '2023-06-30',
          amount: 28500,
          status: 'Livrée',
          items: [
            { name: 'Agrafeuse professionnelle', quantity: 30, price: 950 }
          ],
          isPlaceholder: true
        }
      ],
      'Électronique': [
        {
          id: 'CMD-2023-0188',
          date: '2023-07-10',
          amount: 36000,
          status: 'Livrée',
          items: [
            { name: 'Rallonge électrique 5m', quantity: 30, price: 1200 }
          ],
          isPlaceholder: true
        }
      ],
      'Mobilier': [
        {
          id: 'CMD-2023-0195',
          date: '2023-07-05',
          amount: 185000,
          status: 'Livrée',
          items: [
            { name: 'Chaise de bureau ergonomique', quantity: 10, price: 18500 }
          ],
          isPlaceholder: true
        }
      ],
      'Entretien': [
        {
          id: 'CMD-2023-0210',
          date: '2023-07-10',
          amount: 36000,
          status: 'Livrée',
          items: [
            { name: 'Produit nettoyant multi-surface (5L)', quantity: 20, price: 1800 }
          ],
          isPlaceholder: true
        }
      ]
    };
    
    return fallbackOrdersByCategory[category] || fallbackOrdersByCategory['Papeterie'];
  };

  // Onglets
  const tabs = [
    { id: 'overview', name: 'Vue d\'ensemble' },
    { id: 'products', name: 'Produits' },
    { id: 'orders', name: 'Commandes' },
    { id: 'performance', name: 'Performance' },
    { id: 'negotiations', name: 'Négociations' },
    { id: 'contracts', name: 'Contrats' }
  ];

  // Naviguer vers la page d'édition
  const handleEdit = () => {
    navigate(`/suppliers/${id}/edit`);
  };

  // Naviguer vers la page des négociations
  const handleNegotiations = () => {
    setActiveTab('negotiations');
    toast.success('Redirection vers les négociations avec ce fournisseur');
  };

  // Afficher les étoiles de notation
  const renderRating = (rating) => {
        const numericRating = parseFloat(rating);
    const safeRating = Number.isFinite(numericRating) && numericRating >= 0 ? numericRating : 0;
    const clampedRating = Math.min(safeRating, 5);
    const fullStars = Math.floor(clampedRating);
    const hasHalfStar = clampedRating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    return (
      <div className="flex">
        {Array(fullStars).fill().map((_, i) => (
          <StarIconSolid key={`full-${i}`} className="h-5 w-5 text-amber-400" />
        ))}
        {hasHalfStar && <StarIconOutline className="h-5 w-5 text-amber-400" />}
        {Array(emptyStars).fill().map((_, i) => (
          <StarIconOutline key={`empty-${i}`} className="h-5 w-5 text-amber-400/30" />
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/suppliers')}
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6 text-white" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {supplier.name}
            </h1>
            <p className="text-white/60 mt-1">
              Fournisseur de {supplier.category}
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleNegotiations}
            className="flex items-center space-x-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors"
          >
            <ChatBubbleLeftRightIcon className="h-5 w-5" />
            <span>Négocier</span>
          </button>
          <button
            onClick={handleEdit}
            className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-colors"
          >
            <PencilIcon className="h-5 w-5" />
            <span>Modifier</span>
          </button>
        </div>
      </div>

      {/* Statistiques et métriques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Note globale</h3>
            <StarIconSolid className="h-6 w-6 text-amber-400" />
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold text-white">{supplier.rating}</span>
            <span className="text-white/60 ml-1">/ 5</span>
          </div>
          <div className="mt-2">
            {renderRating(supplier.rating)}
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Livraison à temps</h3>
            <TruckIcon className="h-6 w-6 text-white/70" />
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold text-white">{supplier.onTimeDeliveryRate}%</span>
          </div>
          <div className="mt-2 w-full bg-white/10 rounded-full h-2">
            <div 
              className={`h-2 rounded-full bg-gradient-to-r ${
                supplier.onTimeDeliveryRate >= 90 ? 'from-green-500 to-green-600' :
                supplier.onTimeDeliveryRate >= 80 ? 'from-amber-500 to-amber-600' :
                'from-red-500 to-red-600'
              }`}
              style={{ width: `${supplier.onTimeDeliveryRate}%` }}
            ></div>
          </div>
        </div>
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Délai de livraison</h3>
            <ClockIcon className="h-6 w-6 text-white/70" />
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold text-white">{supplier.avgDeliveryTime}</span>
            <span className="text-white/60 ml-1">jours</span>
          </div>
          <p className="text-white/60 text-sm mt-2">Délai moyen constaté</p>
        </div>
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Dépense totale</h3>
            <CurrencyDollarIcon className="h-6 w-6 text-white/70" />
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold text-white">{(supplier.totalSpent / 1000000).toFixed(1)}M</span>
            <span className="text-white/60 ml-1">DA</span>
          </div>
          <p className="text-white/60 text-sm mt-2">Depuis le {supplier.since}</p>
        </div>
      </div>

      {/* Onglets */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <nav className="flex space-x-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium ${
                activeTab === tab.id
                  ? 'bg-white/10 text-white'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Informations de contact */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-lg font-medium text-white mb-4">Informations de contact</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <BuildingOfficeIcon className="h-5 w-5 text-white/50 mt-0.5 mr-3" />
                <div>
                  <p className="text-white font-medium">{supplier.contactName}</p>
                  <p className="text-white/60 text-sm">Contact principal</p>
                </div>
              </div>
              <div className="flex items-center">
                <PhoneIcon className="h-5 w-5 text-white/50 mr-3" />
                <p className="text-white">{supplier.phone}</p>
              </div>
              <div className="flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-white/50 mr-3" />
                <p className="text-white">{supplier.email}</p>
              </div>
              <div className="flex items-center">
                <MapPinIcon className="h-5 w-5 text-white/50 mr-3" />
                <p className="text-white">{supplier.address}</p>
              </div>
              <div className="flex items-center">
                <DocumentTextIcon className="h-5 w-5 text-white/50 mr-3" />
                <div>
                  <p className="text-white">NIF: {supplier.nif}</p>
                  <p className="text-white">RC: {supplier.rc}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Dernières commandes */}
          <div className="lg:col-span-2 bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-lg font-medium text-white mb-4">Commandes récentes</h3>
            
            {/* Si pas de données, afficher un message */}
            {!supplierPurchases.length && (
              <div className="py-6 flex flex-col items-center justify-center text-white/60">
                {supplier.status === 'Active' ? (
                  <>
                    <ExclamationCircleIcon className="h-12 w-12 mb-2 text-white/40" />
                    <p>Aucune commande trouvée pour ce fournisseur.</p>
                    <p className="text-sm">Ce fournisseur est nouveau et n'a pas encore d'historique de commandes.</p>
                    <button 
                      onClick={() => navigate('/purchases/new')}
                      className="mt-4 px-4 py-2 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl hover:from-green-700 hover:to-blue-700 transition-all duration-300 text-sm"
                    >
                      Passer une commande
                    </button>
                  </>
                ) : (
                  <>
                    <ExclamationCircleIcon className="h-12 w-12 mb-2 text-white/40" />
                    <p>Aucune commande trouvée pour ce fournisseur.</p>
                    <p className="text-sm">Ce fournisseur est inactif.</p>
                  </>
                )}
              </div>
            )}
            
            {/* Si des données existent, afficher le tableau */}
            {supplierPurchases.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-white/10">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Référence</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Montant</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {supplierPurchases.map((purchase, index) => (
                      <tr key={purchase.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-white">{purchase.id || purchase.purchaseNumber}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-white/80">{purchase.date}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-white text-right">
                          {typeof purchase.amount === 'number' 
                            ? purchase.amount.toLocaleString() 
                            : purchase.amount} DA
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            purchase.status === 'Livrée' || purchase.status === 'received' 
                              ? 'bg-green-500/20 text-green-400'
                              : purchase.status === 'En cours' || purchase.status === 'ordered' 
                                ? 'bg-blue-500/20 text-blue-400'
                                : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {purchase.status === 'received' ? 'Livrée' : purchase.status === 'ordered' ? 'En cours' : purchase.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Si le fournisseur est nouveau et n'a pas de commandes, afficher des exemples */}
            {supplierPurchases.length === 0 && supplier.status === 'Active' && supplier.isNew && (
              <div className="mt-4 p-4 rounded-lg bg-white/5 border border-white/10">
                <p className="text-white/70 text-sm mb-2">Exemples de commandes que vous pourrez voir ici :</p>
                <table className="w-full divide-y divide-white/10">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Référence</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-white/50 uppercase tracking-wider">Montant</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {getFallbackOrders(supplier.category).slice(0, 1).map((order) => (
                      <tr key={order.id} className="text-white/40">
                        <td className="px-4 py-2">{order.id}</td>
                        <td className="px-4 py-2">{order.date}</td>
                        <td className="px-4 py-2 text-right">{order.amount.toLocaleString()} DA</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-lg font-medium text-white mb-4">Notes</h3>
            <p className="text-white/80">{supplier.notes || 'Aucune note disponible pour ce fournisseur.'}</p>
          </div>

          {/* Évaluations détaillées */}
          <div className="lg:col-span-2 bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h3 className="text-lg font-medium text-white mb-4">Évaluation détaillée</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-white font-medium">Qualité des produits</span>
                  <span className="text-white/80">{supplier.qualityRating} / 5</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full bg-gradient-to-r from-green-500 to-green-600"
                    style={{ width: `${(supplier.qualityRating / 5) * 100}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-white font-medium">Compétitivité des prix</span>
                  <span className="text-white/80">{supplier.priceCompetitiveness} / 5</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600"
                    style={{ width: `${(supplier.priceCompetitiveness / 5) * 100}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-white font-medium">Communication</span>
                  <span className="text-white/80">{supplier.communicationRating} / 5</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-purple-600"
                    style={{ width: `${(supplier.communicationRating / 5) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Onglet Produits */}
      {activeTab === 'products' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <h3 className="text-xl font-semibold text-white">Produits disponibles</h3>
            <p className="text-white/60 mt-1">Liste des produits proposés par ce fournisseur</p>
          </div>

          {/* Si pas de produits liés à ce fournisseur */}
          {!supplierProducts.length && (
            <div className="py-12 flex flex-col items-center justify-center text-white/60">
              {supplier.status === 'Active' ? (
                <>
                  <ExclamationCircleIcon className="h-12 w-12 mb-2 text-white/40" />
                  <p>Aucun produit n'est encore associé à ce fournisseur.</p>
                  <p className="text-sm mt-1">Associez des produits à ce fournisseur pour les voir apparaître ici.</p>
                  <button 
                    onClick={() => navigate('/products/new')}
                    className="mt-4 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 text-sm"
                  >
                    Ajouter un produit
                  </button>
                </>
              ) : (
                <>
                  <ExclamationCircleIcon className="h-12 w-12 mb-2 text-white/40" />
                  <p>Aucun produit n'est associé à ce fournisseur inactif.</p>
                </>
              )}
            </div>
          )}

          {/* Si des produits sont disponibles */}
          {supplierProducts.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Produit</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Catégorie</th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Prix unitaire</th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Stock</th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {supplierProducts.map((product, index) => (
                    <tr key={product.id || `product-${index}`} className="hover:bg-white/5 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">{product.name}</div>
                        <div className="text-xs text-white/50">{product.id || `PROD-${index+1}`}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{product.category}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-white">
                        {(typeof product.price === 'number' ? product.price.toLocaleString() : product.price) || '-'} DA
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-white">
                        {product.stock || '0'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {(product.stock > 0 || product.inStock) ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                            Disponible
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                            Rupture
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Si le fournisseur est nouveau et n'a pas encore de produits, afficher des exemples */}
          {supplierProducts.length === 0 && supplier.status === 'Active' && supplier.isNew && (
            <div className="m-6 p-4 rounded-lg bg-white/5 border border-white/10">
              <p className="text-white/70 text-sm mb-2">Exemples de produits que vous pourrez voir ici :</p>
              <table className="w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Produit</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Catégorie</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-white/50 uppercase tracking-wider">Prix unitaire</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {getFallbackProducts(supplier.category).slice(0, 2).map((product) => (
                    <tr key={product.id} className="text-white/40">
                      <td className="px-4 py-2">{product.name}</td>
                      <td className="px-4 py-2">{product.category}</td>
                      <td className="px-4 py-2 text-center">{product.price.toLocaleString()} DA</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Onglet Commandes */}
      {activeTab === 'orders' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <h3 className="text-xl font-semibold text-white">Historique des commandes</h3>
            <p className="text-white/60 mt-1">Liste complète des commandes passées à ce fournisseur</p>
          </div>

          {/* Contenu à implémenter */}
          <div className="p-6">
            <p className="text-white/80">Historique complet des commandes à implémenter</p>
          </div>
        </div>
      )}

      {/* Onglet Performance */}
      {activeTab === 'performance' && (
        <div>
          <SupplierPerformance
            suppliers={[supplier]}
            currentSupplierId={id}
          />
        </div>
      )}

      {/* Onglet Négociations */}
      {activeTab === 'negotiations' && (
        <div>
          <SupplierNegotiations 
            suppliers={[supplier]} 
            currentSupplierId={id}
          />
        </div>
      )}

      {/* Onglet Contrats */}
      {activeTab === 'contracts' && (
        <div>
          <SupplierContracts
            suppliers={[supplier]}
            currentSupplierId={id}
          />
        </div>
      )}

      {/* Onglet Comparatif prix supprimé */}
    </div>
  );
};

export default SupplierDetail; 